import { Mcm, McmKeyValPair, McmValue } from '../types/mcm';
import { firstDefined, unsafeDeepCopy } from './utils';
import { camelToSentenceCase } from './string';
import { BareFeedTemplate, FeedTemplate } from '../types/feedTemplate';
import { BareEventTemplate, EventTemplate } from '../types/eventTemplate';
import { BareVenueTemplate, VenueTemplate } from '../types/venueTemplate';
import { BareContentTemplate, ContentTemplate } from '../types/contentTemplate';

function FALLBACK_MCM_VALUE(): McmValue {
  return { visible: true, trending: false };
}

function _resolveMcm(mcm: Mcm | undefined, parentMcm: Mcm | undefined): Mcm {
  if (mcm) return unsafeDeepCopy(mcm);
  if (parentMcm) return unsafeDeepCopy(parentMcm);

  return {
    syncWithTemplate: true,
    endpoints: {
      all: FALLBACK_MCM_VALUE(),
    },
  };
}

export function mcmEndpointToName(edp: string): string {
  return camelToSentenceCase(edp.split('-')[1] || '');
}

export function toggleMcmSync(paramMcm: Mcm | undefined) {
  const mcm = _resolveMcm(paramMcm, undefined);

  mcm.syncWithTemplate = !mcm.syncWithTemplate;

  return mcm;
}

export function toggleMcmEndpoint<T extends keyof McmValue>(
  key: T,
  edp: string,
  edps: string[],
  paramMcm: Mcm | undefined,
  paramParentMcm: Mcm | undefined
) {
  let parentMcm = _resolveMcm(paramParentMcm, undefined);
  let mcm = _resolveMcm(paramMcm, parentMcm);

  const allToggle = !evaluateMcmTruthyAggregate(key, edps, mcm, parentMcm);

  const newMcmEdps: McmKeyValPair = {};

  edps.forEach(e => {
    const ans = evaluateMcmEndpoint(key, e, mcm, parentMcm);
    newMcmEdps[e] = (mcm.endpoints || {})[e] || FALLBACK_MCM_VALUE();
    newMcmEdps[e][key] = ans;
  });

  if (edp === 'all') {
    Object.keys(newMcmEdps).forEach(e => {
      newMcmEdps[e][key] = allToggle;
    });
  } else {
    newMcmEdps[edp][key] = !newMcmEdps[edp][key];
  }

  mcm.endpoints = newMcmEdps;
  mcm.syncWithTemplate = false;

  return mcm;
}

function _evaluateHelper<T extends keyof McmValue>(
  fallback: boolean,
  key: T,
  edp: string,
  mcm: Mcm
): McmValue[T] {
  const endpoints = mcm.endpoints || {};
  return !!firstDefined(fallback, (endpoints.all || {})[key], (endpoints[edp] || {})[key]);
}

export function evaluateMcmEndpoint<T extends keyof McmValue>(
  key: T,
  edp: string,
  paramMcm: Mcm | undefined,
  paramTemplateMcm: Mcm | undefined
): McmValue[T] {
  const templateMcm = _resolveMcm(paramTemplateMcm, undefined);
  const mcm = _resolveMcm(paramMcm, templateMcm);

  const parentVal = _evaluateHelper(FALLBACK_MCM_VALUE()[key], key, edp, templateMcm);
  const sourceVal = _evaluateHelper(parentVal, key, edp, mcm);

  return !mcm || mcm.syncWithTemplate ? parentVal : sourceVal;
}

export function evaluateMcmTruthyAggregate<T extends keyof McmValue>(
  key: T,
  edps: string[],
  mcm: Mcm | undefined,
  templateMcm: Mcm | undefined
): boolean | null {
  let trueCount = 0;
  let falseCount = 0;

  edps.forEach(edp => {
    const ans = evaluateMcmEndpoint(key, edp, mcm, templateMcm);

    if (ans) trueCount += 1;
    else falseCount += 1;
  });

  if (trueCount > 0 && falseCount === 0) {
    return true;
  } else if (falseCount > 0 && trueCount === 0) {
    return false;
  } else if (edps.length === 0) {
    return true;
  } else {
    return null;
  }
}

export function getPrintProgramMcmEndpoint(feed?: BareFeedTemplate | FeedTemplate): string {
  return feed?.mcmKey || 'newspaper-printProgram';
}

export function isPrintProgramMcmEndpoint(mcmKey: string, feeds: BareFeedTemplate[]) {
  return feeds.some(feed => getPrintProgramMcmEndpoint(feed) === mcmKey);
}

export function isHiddenEventDueToBasicFilters(
  et?: BareEventTemplate | EventTemplate,
  vt?: BareVenueTemplate | VenueTemplate
): boolean {
  return !et || et.archived || !vt || vt.archived || et.crewOnly || et.isPrivate;
}

export function isHiddenVenueDueToBasicFilters(vt?: BareVenueTemplate | VenueTemplate): boolean {
  return !vt || vt.archived || !!vt.isHidden;
}

export function isHiddenContentDueToBasicFilters(
  ct?: BareContentTemplate | ContentTemplate
): boolean {
  return !ct || !!ct.archived;
}
